import "./App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import AppBanner from "./components/AppBanner";

// Critical path - load immediately
import LandingPage from "./components/landing/LandingPage";
import NotFound from "./components/pages/NotFound";

// Lazy loaded pages
const PrivacyPolicy = lazy(() => import("./components/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./components/pages/TermsConditions"));
const SecurityDataProtection = lazy(() => import("./components/pages/SecurityDataProtection"));
const CookiePolicy = lazy(() => import("./components/pages/CookiePolicy"));
const CommunityGroups = lazy(() => import("./components/pages/CommunityGroups"));
const LocationPage = lazy(() => import("./components/pages/LocationPage"));
const BuildingTypePage = lazy(() => import("./components/pages/BuildingTypePage"));
const SuburbPage = lazy(() => import("./components/pages/SuburbPage"));
const About = lazy(() => import("./components/pages/About"));
const Contact = lazy(() => import("./components/pages/Contact"));

// Blog
const BlogIndex = lazy(() => import("./components/pages/BlogIndex"));
const BlogPost = lazy(() => import("./components/pages/BlogPost"));

// Guides (Pillar Pages)
const GuidesIndex = lazy(() => import("./components/pages/GuidesIndex"));
const HomeBuildingProcess = lazy(() => import("./components/pages/pillars/HomeBuildingProcess"));
const ChoosingBuilder = lazy(() => import("./components/pages/pillars/ChoosingBuilder"));
const BuildingCosts = lazy(() => import("./components/pages/pillars/BuildingCosts"));
const LandDesignFeasibility = lazy(() => import("./components/pages/pillars/LandDesignFeasibility"));
const CommonMistakes = lazy(() => import("./components/pages/pillars/CommonMistakes"));
const BuildingWithTools = lazy(() => import("./components/pages/pillars/BuildingWithTools"));

// Tools
const ToolsIndex = lazy(() => import("./components/pages/ToolsIndex"));
const CostTracker = lazy(() => import("./components/pages/tools/CostTracker"));
const Milestones = lazy(() => import("./components/pages/tools/Milestones"));
const Checklists = lazy(() => import("./components/pages/tools/Checklists"));
const GovernmentResources = lazy(() => import("./components/pages/tools/GovernmentResources"));
const FindBusiness = lazy(() => import("./components/pages/tools/FindBusiness"));
const Documents = lazy(() => import("./components/pages/tools/Documents"));
const LicenceCheck = lazy(() => import("./components/pages/tools/LicenceCheck"));
const InsuranceCheck = lazy(() => import("./components/pages/tools/InsuranceCheck"));
const SolarOrientation = lazy(() => import("./components/pages/tools/SolarOrientation"));
const DefectsScanner = lazy(() => import("./components/pages/tools/DefectsScanner"));
const BuilderMatch = lazy(() => import("./components/pages/tools/BuilderMatch"));
const SnapStyle = lazy(() => import("./components/pages/tools/SnapStyle"));
const QuoteComparison = lazy(() => import("./components/pages/tools/QuoteComparison"));
const FacadeVisualiser = lazy(() => import("./components/pages/tools/FacadeVisualiser"));
const ProjectHealth = lazy(() => import("./components/pages/tools/ProjectHealth"));
const BuildPilotIntelligence = lazy(() => import("./components/pages/tools/BuildPilotIntelligence"));

// State-specific tool pages & Comparisons
const StateToolPage = lazy(() => import("./components/pages/StateToolPage"));
const ComparisonPage = lazy(() => import("./components/pages/ComparisonPage").then(m => ({ default: m.default })));
const ComparisonIndex = lazy(() => import("./components/pages/ComparisonPage").then(m => ({ default: m.ComparisonIndex })));
const ProjectTypeStatePage = lazy(() => import("./components/pages/ProjectTypeStatePage"));
const GSCDashboard = lazy(() => import("./components/pages/GSCDashboard"));
const Advertise = lazy(() => import("./components/pages/Advertise"));
const CostToBuild = lazy(() => import("./components/pages/CostToBuild"));
const BuildingChecklist = lazy(() => import("./components/pages/BuildingChecklist"));
const RenovationChecklist = lazy(() => import("./components/pages/RenovationChecklist"));
const OwnerBuilderGuide = lazy(() => import("./components/pages/OwnerBuilderGuide"));
const EnergyEfficiency7Star = lazy(() => import("./components/pages/EnergyEfficiency7Star"));
const AllElectricHome = lazy(() => import("./components/pages/AllElectricHome"));
const KnockdownRebuildCosts = lazy(() => import("./components/pages/KnockdownRebuild"));
const FixedVsCostPlus = lazy(() => import("./components/pages/FixedVsCostPlus"));

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/security" element={<SecurityDataProtection />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/community" element={<CommunityGroups />} />
            
            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* Guides (Pillar Pages) */}
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/building-process" element={<HomeBuildingProcess />} />
            <Route path="/guides/choosing-a-builder" element={<ChoosingBuilder />} />
            <Route path="/guides/building-costs" element={<BuildingCosts />} />
            <Route path="/guides/land-design-feasibility" element={<LandDesignFeasibility />} />
            <Route path="/guides/common-mistakes" element={<CommonMistakes />} />
            <Route path="/guides/building-tools" element={<BuildingWithTools />} />
            
            {/* Tools */}
            <Route path="/tools" element={<ToolsIndex />} />
            <Route path="/tools/cost-tracker" element={<CostTracker />} />
            <Route path="/tools/milestones" element={<Milestones />} />
            <Route path="/tools/checklists" element={<Checklists />} />
            <Route path="/tools/government-resources" element={<GovernmentResources />} />
            <Route path="/tools/find-business" element={<FindBusiness />} />
            <Route path="/tools/documents" element={<Documents />} />
            <Route path="/tools/licence-check" element={<LicenceCheck />} />
            <Route path="/tools/insurance-check" element={<InsuranceCheck />} />
            <Route path="/tools/solar-orientation" element={<SolarOrientation />} />
            <Route path="/tools/defects-scanner" element={<DefectsScanner />} />
            <Route path="/tools/builder-match" element={<BuilderMatch />} />
            <Route path="/tools/snap-style" element={<SnapStyle />} />
            <Route path="/tools/quote-comparison" element={<QuoteComparison />} />
            <Route path="/tools/facade-visualiser" element={<FacadeVisualiser />} />
            <Route path="/tools/project-health" element={<ProjectHealth />} />
            <Route path="/tools/buildpilot-intelligence" element={<BuildPilotIntelligence />} />
            
            {/* State-specific tool pages */}
            <Route path="/tools/:toolSlug/:stateSlug" element={<StateToolPage />} />

            {/* State-specific project type pages */}
            <Route path="/projects/:projectSlug/:stateSlug" element={<ProjectTypeStatePage />} />

            {/* Comparison pages */}
            <Route path="/compare" element={<ComparisonIndex />} />
            <Route path="/compare/:slug" element={<ComparisonPage />} />
            
            {/* Location-based SEO pages */}
            <Route path="/building-in-adelaide" element={<LocationPage location="adelaide" />} />
            <Route path="/building-in-melbourne" element={<LocationPage location="melbourne" />} />
            <Route path="/building-in-sydney" element={<LocationPage location="sydney" />} />
            <Route path="/building-in-brisbane" element={<LocationPage location="brisbane" />} />
            <Route path="/building-in-perth" element={<LocationPage location="perth" />} />
            {/* Building type SEO pages */}
            <Route path="/renovating-your-home" element={<BuildingTypePage buildingType="renovating-your-home" />} />
            <Route path="/home-extensions" element={<BuildingTypePage buildingType="home-extensions" />} />
            <Route path="/knockdown-rebuild" element={<BuildingTypePage buildingType="knockdown-rebuild" />} />
            <Route path="/owner-builder" element={<BuildingTypePage buildingType="owner-builder" />} />
            <Route path="/pergolas-verandahs" element={<BuildingTypePage buildingType="pergolas-verandahs" />} />
            <Route path="/decking" element={<BuildingTypePage buildingType="decking" />} />
            <Route path="/swimming-pools" element={<BuildingTypePage buildingType="swimming-pools" />} />
            <Route path="/landscaping" element={<BuildingTypePage buildingType="landscaping" />} />
            <Route path="/sheds-garages" element={<BuildingTypePage buildingType="sheds-garages" />} />
            {/* Suburb-level SEO pages - Adelaide Northern (Playford) */}
            <Route path="/building-in-angle-vale" element={<SuburbPage suburb="angle-vale" />} />
            <Route path="/building-in-munno-para" element={<SuburbPage suburb="munno-para" />} />
            <Route path="/building-in-craigmore" element={<SuburbPage suburb="craigmore" />} />
            <Route path="/building-in-virginia" element={<SuburbPage suburb="virginia" />} />
            <Route path="/building-in-andrews-farm" element={<SuburbPage suburb="andrews-farm" />} />
            <Route path="/building-in-blakeview" element={<SuburbPage suburb="blakeview" />} />
            <Route path="/building-in-smithfield" element={<SuburbPage suburb="smithfield" />} />
            <Route path="/building-in-elizabeth" element={<SuburbPage suburb="elizabeth" />} />
            {/* Suburb-level SEO pages - Adelaide Northern (Salisbury) */}
            <Route path="/building-in-paralowie" element={<SuburbPage suburb="paralowie" />} />
            <Route path="/building-in-salisbury" element={<SuburbPage suburb="salisbury" />} />
            <Route path="/building-in-mawson-lakes" element={<SuburbPage suburb="mawson-lakes" />} />
            <Route path="/building-in-pooraka" element={<SuburbPage suburb="pooraka" />} />
            {/* Suburb-level SEO pages - Adelaide Northern (Gawler/Plains) */}
            <Route path="/building-in-gawler" element={<SuburbPage suburb="gawler" />} />
            <Route path="/building-in-two-wells" element={<SuburbPage suburb="two-wells" />} />
            {/* Suburb-level SEO pages - Adelaide Southern (Onkaparinga) */}
            <Route path="/building-in-seaford" element={<SuburbPage suburb="seaford" />} />
            <Route path="/building-in-aldinga-beach" element={<SuburbPage suburb="aldinga-beach" />} />
            <Route path="/building-in-morphett-vale" element={<SuburbPage suburb="morphett-vale" />} />
            <Route path="/building-in-hackham" element={<SuburbPage suburb="hackham" />} />
            <Route path="/building-in-christie-downs" element={<SuburbPage suburb="christie-downs" />} />
            <Route path="/building-in-moana" element={<SuburbPage suburb="moana" />} />
            {/* Suburb-level SEO pages - Adelaide Hills */}
            <Route path="/building-in-mount-barker" element={<SuburbPage suburb="mount-barker" />} />
            {/* Suburb-level SEO pages - Melbourne Western (Wyndham) */}
            <Route path="/building-in-tarneit" element={<SuburbPage suburb="tarneit" />} />
            <Route path="/building-in-wyndham-vale" element={<SuburbPage suburb="wyndham-vale" />} />
            <Route path="/building-in-truganina" element={<SuburbPage suburb="truganina" />} />
            <Route path="/building-in-point-cook" element={<SuburbPage suburb="point-cook" />} />
            <Route path="/building-in-werribee" element={<SuburbPage suburb="werribee" />} />
            {/* Suburb-level SEO pages - Melbourne Northern (Hume/Whittlesea) */}
            <Route path="/building-in-craigieburn-vic" element={<SuburbPage suburb="craigieburn" />} />
            <Route path="/building-in-mickleham" element={<SuburbPage suburb="mickleham" />} />
            <Route path="/building-in-donnybrook-vic" element={<SuburbPage suburb="donnybrook-vic" />} />
            <Route path="/building-in-kalkallo" element={<SuburbPage suburb="kalkallo" />} />
            <Route path="/building-in-wollert" element={<SuburbPage suburb="wollert" />} />
            <Route path="/building-in-south-morang" element={<SuburbPage suburb="south-morang" />} />
            <Route path="/building-in-mernda" element={<SuburbPage suburb="mernda" />} />
            <Route path="/building-in-roxburgh-park" element={<SuburbPage suburb="roxburgh-park" />} />
            <Route path="/building-in-beveridge" element={<SuburbPage suburb="beveridge" />} />
            {/* Suburb-level SEO pages - Melbourne South East (Casey/Cardinia) */}
            <Route path="/building-in-clyde-north" element={<SuburbPage suburb="clyde-north" />} />
            <Route path="/building-in-clyde" element={<SuburbPage suburb="clyde" />} />
            <Route path="/building-in-cranbourne" element={<SuburbPage suburb="cranbourne" />} />
            <Route path="/building-in-officer" element={<SuburbPage suburb="officer" />} />
            <Route path="/building-in-pakenham" element={<SuburbPage suburb="pakenham" />} />
            <Route path="/building-in-berwick" element={<SuburbPage suburb="berwick" />} />
            {/* Suburb-level SEO pages - Sydney South West (Camden/Liverpool) */}
            <Route path="/building-in-oran-park" element={<SuburbPage suburb="oran-park" />} />
            <Route path="/building-in-leppington" element={<SuburbPage suburb="leppington" />} />
            <Route path="/building-in-gledswood-hills" element={<SuburbPage suburb="gledswood-hills" />} />
            <Route path="/building-in-gregory-hills" element={<SuburbPage suburb="gregory-hills" />} />
            <Route path="/building-in-austral" element={<SuburbPage suburb="austral" />} />
            {/* Suburb-level SEO pages - Sydney North West (Blacktown/Hills) */}
            <Route path="/building-in-marsden-park" element={<SuburbPage suburb="marsden-park" />} />
            <Route path="/building-in-box-hill-nsw" element={<SuburbPage suburb="box-hill-nsw" />} />
            <Route path="/building-in-schofields" element={<SuburbPage suburb="schofields" />} />
            <Route path="/building-in-riverstone" element={<SuburbPage suburb="riverstone" />} />
            {/* Suburb-level SEO pages - Brisbane South (Logan) */}
            <Route path="/building-in-yarrabilba" element={<SuburbPage suburb="yarrabilba" />} />
            <Route path="/building-in-logan-reserve" element={<SuburbPage suburb="logan-reserve" />} />
            <Route path="/building-in-park-ridge" element={<SuburbPage suburb="park-ridge" />} />
            <Route path="/building-in-flagstone" element={<SuburbPage suburb="flagstone" />} />
            {/* Suburb-level SEO pages - Brisbane South West (Ipswich) */}
            <Route path="/building-in-springfield" element={<SuburbPage suburb="springfield" />} />
            <Route path="/building-in-ripley" element={<SuburbPage suburb="ripley" />} />
            {/* Suburb-level SEO pages - Brisbane North (Moreton Bay) */}
            <Route path="/building-in-north-lakes" element={<SuburbPage suburb="north-lakes" />} />
            <Route path="/building-in-narangba" element={<SuburbPage suburb="narangba" />} />
            <Route path="/building-in-burpengary" element={<SuburbPage suburb="burpengary" />} />
            {/* Suburb-level SEO pages - Perth South (Rockingham/Armadale) */}
            <Route path="/building-in-baldivis" element={<SuburbPage suburb="baldivis" />} />
            <Route path="/building-in-rockingham" element={<SuburbPage suburb="rockingham" />} />
            <Route path="/building-in-byford" element={<SuburbPage suburb="byford" />} />
            <Route path="/building-in-piara-waters" element={<SuburbPage suburb="piara-waters" />} />
            {/* Suburb-level SEO pages - Perth North (Wanneroo/Swan) */}
            <Route path="/building-in-ellenbrook" element={<SuburbPage suburb="ellenbrook" />} />
            <Route path="/building-in-alkimos" element={<SuburbPage suburb="alkimos" />} />
            <Route path="/building-in-yanchep" element={<SuburbPage suburb="yanchep" />} />
            <Route path="/building-in-wanneroo" element={<SuburbPage suburb="wanneroo" />} />
            {/* Additional Sydney suburbs */}
            <Route path="/building-in-edmondson-park" element={<SuburbPage suburb="edmondson-park" />} />
            <Route path="/building-in-jordan-springs" element={<SuburbPage suburb="jordan-springs" />} />
            <Route path="/building-in-kellyville" element={<SuburbPage suburb="kellyville" />} />
            <Route path="/building-in-the-ponds" element={<SuburbPage suburb="the-ponds" />} />
            <Route path="/building-in-rouse-hill" element={<SuburbPage suburb="rouse-hill" />} />
            <Route path="/building-in-narellan" element={<SuburbPage suburb="narellan" />} />
            <Route path="/building-in-ingleburn" element={<SuburbPage suburb="ingleburn" />} />
            <Route path="/building-in-campbelltown" element={<SuburbPage suburb="campbelltown" />} />
            <Route path="/building-in-penrith" element={<SuburbPage suburb="penrith" />} />
            {/* Additional Brisbane suburbs */}
            <Route path="/building-in-pimpama" element={<SuburbPage suburb="pimpama" />} />
            <Route path="/building-in-coomera" element={<SuburbPage suburb="coomera" />} />
            <Route path="/building-in-ormeau" element={<SuburbPage suburb="ormeau" />} />
            <Route path="/building-in-caboolture" element={<SuburbPage suburb="caboolture" />} />
            <Route path="/building-in-redbank-plains" element={<SuburbPage suburb="redbank-plains" />} />
            <Route path="/building-in-augustine-heights" element={<SuburbPage suburb="augustine-heights" />} />
            <Route path="/building-in-jimboomba" element={<SuburbPage suburb="jimboomba" />} />
            <Route path="/building-in-mango-hill" element={<SuburbPage suburb="mango-hill" />} />
            {/* Additional Perth suburbs */}
            <Route path="/building-in-harrisdale" element={<SuburbPage suburb="harrisdale" />} />
            <Route path="/building-in-wellard" element={<SuburbPage suburb="wellard" />} />
            <Route path="/building-in-banksia-grove" element={<SuburbPage suburb="banksia-grove" />} />
            <Route path="/building-in-brabham" element={<SuburbPage suburb="brabham" />} />
            <Route path="/building-in-two-rocks" element={<SuburbPage suburb="two-rocks" />} />
            <Route path="/building-in-treeby" element={<SuburbPage suburb="treeby" />} />
            <Route path="/building-in-armadale-wa" element={<SuburbPage suburb="armadale-wa" />} />
            <Route path="/building-in-butler" element={<SuburbPage suburb="butler" />} />
            <Route path="/building-in-secret-harbour" element={<SuburbPage suburb="secret-harbour" />} />
            {/* Advertise */}
            <Route path="/advertise" element={<Advertise />} />

            {/* High-Intent Landing Pages */}
            <Route path="/cost-to-build" element={<CostToBuild />} />
            <Route path="/building-checklist" element={<BuildingChecklist />} />
            <Route path="/renovation-checklist" element={<RenovationChecklist />} />
            <Route path="/owner-builder-guide" element={<OwnerBuilderGuide />} />
            <Route path="/7-star-energy-rating" element={<EnergyEfficiency7Star />} />
            <Route path="/all-electric-home" element={<AllElectricHome />} />
            <Route path="/knockdown-rebuild-costs" element={<KnockdownRebuildCosts />} />
            <Route path="/fixed-price-vs-cost-plus" element={<FixedVsCostPlus />} />

            {/* Admin Dashboard - Private (noindex) */}
            <Route path="/admin/gsc" element={<GSCDashboard />} />
            {/* 404 catch-all route - must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <CookieConsent />
          <AppBanner />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
